import { useCallback, useMemo } from 'react';

import {
  createSpec,
  specTypes,
  useFetch,
  useDataRefId,
  useRefetch,
  useMutation,
} from '@bingads-webui-react/overreact';

import { environmentLookupFn } from '../../../environment/environment-instance';
import {
  fetchRequestContract,
  fetchResponseContract,
} from '../spec/fetch';

import {
  mutationRequestContract,
  mutationResponseContract,
} from '../spec/mutation';
import type {
  FetchRefetchMutationConfig,
  MutationRequestConfig,
  ReadParameters,
  ReadRequestConfig,
  User,
  UserMutationData,
  UserVariables,
} from '../../../types';

const fetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.FETCH, null, environmentLookupFn
);
const refetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.REFETCH, null, environmentLookupFn
);
const mutationSpec = createSpec(
  mutationRequestContract, mutationResponseContract,
  specTypes.MUTATION, null, environmentLookupFn
);

const DEFAULT_CONFIG_VALLUE = {};
const DEFAULT_CONFIG: FetchRefetchMutationConfig<
  User,
  UserVariables,
  UserMutationData
> = DEFAULT_CONFIG_VALLUE;


export function useUser(
  dataLocator: UserVariables,
  config: FetchRefetchMutationConfig<User, UserVariables, UserMutationData> = DEFAULT_CONFIG
) {
  const dataRefId = useDataRefId(null, environmentLookupFn);

  const defaultConfig = useMemo(() => ({

    fetch: config.fetch || DEFAULT_CONFIG_VALLUE,

    refetch: config.refetch || DEFAULT_CONFIG_VALLUE,

    mutation: config.mutation || (
      DEFAULT_CONFIG_VALLUE as NonNullable<typeof config.mutation>
    ),
  }), [config]);

  const { fetch: fetchConfig, refetch, mutation } = defaultConfig;

  const fetch = useFetch(dataRefId, fetchSpec, dataLocator, fetchConfig) as [
    User | undefined,
    unknown,
  ];
  const refetchFn = useRefetch(dataRefId, refetchSpec, refetch);

  const read = useCallback((
    param: ReadParameters<UserVariables> = {},
    requestConfig?: ReadRequestConfig<User, UserVariables>
  ) => {
    refetchFn({
      variables: { ...dataLocator, ...param.variables },
    }, requestConfig);
  }, [dataLocator, refetchFn]);

  const updateFn = useMutation(dataRefId, mutationSpec, mutation.update);

  const update = useCallback((
    data: UserMutationData,
    ...rest: Array<MutationRequestConfig<User, UserMutationData>>
  ) => {
    updateFn({ ...dataLocator }, data, ...rest);
  }, [dataLocator, updateFn]);

  const [data, error] = fetch;

  return [{ data, error }, { read, update }];
}
